import * as THREE from 'three';
import { Vec3, vec, add, mul, sub, dot, normalize } from '../core/vec';
import { normAngle } from '../collab/follow';
import type { PeerView } from '../../server/protocol';

/**
 * SketchUp-artige Kamera mit Z nach oben.
 * - Umlauf (Orbit) dreht um den Punkt unter dem Cursor beim Greifen
 * - Schwenken (Pan) hält den gegriffenen Punkt exakt unter dem Cursor
 * - Zoom (Mausrad/Zoom-Werkzeug) fährt auf den Punkt unter dem Cursor zu
 * - Standardansichten über Winkel setzen
 */
export class OrbitController {
  camera: THREE.PerspectiveCamera;
  target: Vec3 = vec(2, 2, 0.5);
  /** Distanz Kamera→Target */
  distance = 12;
  /** Azimut um Z (0 = +X), Elevation über Horizont */
  azimuth = -Math.PI / 2 - Math.PI / 4;
  elevation = Math.PI / 6;

  /** Liefert den Modellpunkt unter NDC-Koordinaten (Flächen-Treffer), null = nichts getroffen. */
  picker: ((ndcX: number, ndcY: number) => Vec3 | null) | null = null;

  private canvas: HTMLCanvasElement;
  private orbitPivot: Vec3 | null = null;
  private panDepth = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.02, 1000);
    this.camera.up.set(0, 0, 1);
    this.updateCamera();
  }

  /** Kamera-Position aus Kugelkoordinaten neu berechnen. */
  updateCamera(): void {
    const cosE = Math.cos(this.elevation);
    const offset = vec(
      this.distance * cosE * Math.cos(this.azimuth),
      this.distance * cosE * Math.sin(this.azimuth),
      this.distance * Math.sin(this.elevation)
    );
    this.camera.position.set(this.target.x + offset.x, this.target.y + offset.y, this.target.z + offset.z);
    this.camera.lookAt(this.target.x, this.target.y, this.target.z);
    this.camera.updateMatrixWorld();
  }

  /** Aktuelle Ansicht als übertragbaren Zustand erfassen (Folgen-Modus). */
  captureView(): PeerView {
    return {
      target: [this.target.x, this.target.y, this.target.z],
      distance: this.distance,
      azimuth: normAngle(this.azimuth),
      elevation: this.elevation
    };
  }

  /** Übertragene Ansicht übernehmen (Folgen-Modus) — Ziel, Abstand und Blickwinkel. */
  applyView(v: PeerView): void {
    this.orbitPivot = null;
    this.panDepth = 0;
    this.target = vec(v.target[0], v.target[1], v.target[2]);
    this.distance = THREE.MathUtils.clamp(v.distance, 0.05, 800);
    this.azimuth = normAngle(v.azimuth);
    this.elevation = THREE.MathUtils.clamp(v.elevation, -Math.PI / 2 + 0.001, Math.PI / 2 - 0.001);
    this.updateCamera();
  }

  /** Kameraposition (Welt). */
  position(): Vec3 {
    const p = this.camera.position;
    return vec(p.x, p.y, p.z);
  }

  /**
   * Punkt unter dem Cursor: getroffene Fläche, sonst Boden (Z = 0) in sinnvoller
   * Entfernung, sonst Punkt auf der bildschirmparallelen Ebene durch das Ziel.
   */
  pointAt(ndcX: number, ndcY: number): Vec3 {
    const hit = this.picker?.(ndcX, ndcY);
    if (hit) return hit;
    const { origin, dir } = this.rayFromNdc(ndcX, ndcY);
    if (Math.abs(dir.z) > 1e-6) {
      const t = -origin.z / dir.z;
      // Boden nutzen, solange er nicht fast am Horizont liegt (unabhängig vom aktuellen Zoomabstand)
      if (t > 0 && t < Math.max(this.distance * 8, 60)) return add(origin, mul(dir, t));
    }
    // Ebene durch das Ziel senkrecht zur Blickrichtung
    const view = mul(this.viewDirection(), -1);
    const denom = dot(dir, view);
    const t = denom > 1e-6 ? dot(sub(this.target, origin), view) / denom : this.distance;
    return add(origin, mul(dir, t));
  }

  /** Tiefe eines Punkts entlang der Blickachse. */
  private depthOf(p: Vec3): number {
    return dot(sub(p, this.position()), mul(this.viewDirection(), -1));
  }

  /**
   * Ziel auf der Blickachse in Tiefe `depth` legen — die Ansicht bleibt identisch,
   * aber Zoom/Grenzen beziehen sich danach auf diese Tiefe.
   */
  private retarget(depth: number): void {
    const d = Math.max(0.05, depth);
    this.target = add(this.position(), mul(this.viewDirection(), -d));
    this.distance = d;
  }

  // ---------- Umlauf ----------

  /** Umlauf beginnen: Drehpunkt = Punkt unter dem Cursor (wie SketchUp). */
  beginOrbit(ndcX: number, ndcY: number): void {
    this.orbitPivot = this.pointAt(ndcX, ndcY);
  }

  endOrbit(): void {
    this.orbitPivot = null;
  }

  /** Umlauf um den Drehpunkt (Standard: Ziel). Ziehen rechts dreht das Modell nach rechts. */
  orbit(dx: number, dy: number): void {
    const pivot = this.orbitPivot ?? this.target;
    const dAz = -dx * 0.006;
    const newEl = THREE.MathUtils.clamp(this.elevation + dy * 0.006, -Math.PI / 2 + 0.001, Math.PI / 2 - 0.001);
    const dEl = newEl - this.elevation;

    // Ziel starr mit der Kamera um den Drehpunkt mitdrehen: erst Neigung (um Kamera-rechts), dann Azimut (um Z)
    const rel = new THREE.Vector3(this.target.x - pivot.x, this.target.y - pivot.y, this.target.z - pivot.z);
    const tiltAxis = new THREE.Vector3(Math.sin(this.azimuth), -Math.cos(this.azimuth), 0);
    rel.applyAxisAngle(tiltAxis, dEl);
    rel.applyAxisAngle(new THREE.Vector3(0, 0, 1), dAz);
    this.target = vec(pivot.x + rel.x, pivot.y + rel.y, pivot.z + rel.z);
    this.azimuth += dAz;
    this.elevation = newEl;
    this.updateCamera();
  }

  // ---------- Schwenken ----------

  /** Schwenken beginnen: der Punkt unter dem Cursor folgt danach exakt der Maus. */
  beginPan(ndcX: number, ndcY: number): void {
    this.panDepth = Math.max(0.05, this.depthOf(this.pointAt(ndcX, ndcY)));
  }

  /** Bildschirmparalleles Schwenken um dx/dy Pixel. */
  pan(dx: number, dy: number): void {
    const depth = this.panDepth > 0 ? this.panDepth : this.distance;
    const worldPerPx = (2 * depth * Math.tan((this.camera.fov * Math.PI) / 360)) / Math.max(1, this.canvas.clientHeight);
    const r = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0);
    const u = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1);
    const shift = add(mul(vec(r.x, r.y, r.z), -dx * worldPerPx), mul(vec(u.x, u.y, u.z), dy * worldPerPx));
    this.target = add(this.target, shift);
    if (this.orbitPivot) this.orbitPivot = add(this.orbitPivot, shift);
    this.updateCamera();
  }

  endPan(): void {
    this.panDepth = 0;
  }

  // ---------- Zoom ----------

  /** Zoom um das Ziel (factor < 1 = hinein). */
  zoom(factor: number): void {
    this.distance = THREE.MathUtils.clamp(this.distance * factor, 0.05, 800);
    this.updateCamera();
  }

  /** Zoom auf den Punkt unter dem Cursor zu — er bleibt dabei an derselben Bildschirmstelle. */
  zoomAt(factor: number, ndcX: number, ndcY: number): void {
    const p = this.pointAt(ndcX, ndcY);
    const depth = this.depthOf(p);
    if (depth <= 0.01) {
      this.zoom(factor);
      return;
    }
    this.retarget(depth);
    const next = THREE.MathUtils.clamp(this.distance * factor, 0.05, 800);
    const f = next / this.distance;
    // Kamera und Ziel um p skalieren: Blickrichtung bleibt, p bleibt unter dem Cursor
    this.target = add(p, mul(sub(this.target, p), f));
    this.distance = next;
    this.updateCamera();
  }

  /** Ansicht auf den Punkt unter dem Cursor zentrieren (Doppelklick Mausrad). */
  centerAt(ndcX: number, ndcY: number): void {
    const p = this.pointAt(ndcX, ndcY);
    const depth = this.depthOf(p);
    if (depth <= 0.01) return;
    // Parallel verschieben, sodass p in der Bildmitte liegt; Ziel auf p setzen
    this.target = p;
    this.distance = depth;
    this.updateCamera();
  }

  zoomExtents(points: Vec3[]): void {
    this.orbitPivot = null;
    this.panDepth = 0;
    if (points.length === 0) {
      this.target = vec(2, 2, 0.5);
      this.distance = 12;
    } else {
      let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
      for (const p of points) {
        minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); minZ = Math.min(minZ, p.z);
        maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y); maxZ = Math.max(maxZ, p.z);
      }
      const c = vec((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
      const radius = Math.max(1, Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) / 2);
      this.target = c;
      // Engere der beiden Bildachsen maßgeblich (schmale/hochkant Fenster schneiden sonst seitlich ab)
      const halfV = (this.camera.fov * Math.PI) / 360;
      const halfH = Math.atan(Math.tan(halfV) * Math.max(0.1, this.camera.aspect));
      const half = Math.min(halfV, halfH);
      this.distance = THREE.MathUtils.clamp((radius / Math.sin(half)) * 1.1, 0.5, 400);
    }
    this.updateCamera();
  }

  /** Standardansicht setzen. view: front|back|left|right|top|bottom|iso */
  setView(view: string): void {
    switch (view) {
      case 'front': this.azimuth = -Math.PI / 2; this.elevation = 0; break;
      case 'back': this.azimuth = Math.PI / 2; this.elevation = 0; break;
      case 'right': this.azimuth = 0; this.elevation = 0; break;
      case 'left': this.azimuth = Math.PI; this.elevation = 0; break;
      case 'top': this.azimuth = -Math.PI / 2; this.elevation = Math.PI / 2 - 0.001; break;
      case 'bottom': this.azimuth = -Math.PI / 2; this.elevation = -Math.PI / 2 + 0.001; break;
      case 'iso': this.azimuth = -Math.PI / 2 - Math.PI / 4; this.elevation = Math.PI / 6; break;
    }
    this.updateCamera();
  }

  /** Blickstrahl (Welt) zu normierten Gerätekoordinaten. */
  rayFromNdc(ndcX: number, ndcY: number): { origin: Vec3; dir: Vec3 } {
    const rc = new THREE.Raycaster();
    rc.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.camera);
    const o = rc.ray.origin;
    const d = rc.ray.direction;
    return { origin: vec(o.x, o.y, o.z), dir: vec(d.x, d.y, d.z) };
  }

  /** Blickrichtung der Kamera (Welt, normalisiert) — Normale einer bildschirmparallelen Ebene. */
  viewDirection(): Vec3 {
    return normalize(vec(
      this.camera.position.x - this.target.x,
      this.camera.position.y - this.target.y,
      this.camera.position.z - this.target.z
    ));
  }

  /** Projektion Welt→Bildschirm-Pixel (CSS px). */
  projectToScreen(p: Vec3): { x: number; y: number } {
    const v = new THREE.Vector3(p.x, p.y, p.z).project(this.camera);
    return {
      x: ((v.x + 1) / 2) * this.canvas.clientWidth,
      y: ((1 - v.y) / 2) * this.canvas.clientHeight
    };
  }

  /** Distanz eines Weltpunkts zum Cursor in Bildschirm-Pixeln. */
  screenDistance(p: Vec3, cursorX: number, cursorY: number): number {
    const s = this.projectToScreen(p);
    return Math.hypot(s.x - cursorX, s.y - cursorY);
  }
}
